import * as React from 'react'
import {
  appIdSort,
  ApplicationData,
  environmentsOptions,
  getApplicationName,
  pceReportsButtons,
  ReportsApplication,
  reportsButtons,
  reportsTimeOptions,
  ReportsTypes
} from './model'
import { ResourceSelectorContainer } from '../ResourceSelector/model'
import { getResolvedFileSize } from '../../util/FileSizeHelper'
import { getBuiltInURL } from '../Sidebar/Urls'
import { isPCE } from '../../util/environments'
import { Environment } from '../../remote-api/CoreServicesRemoteApi'
import { RawDataPage } from '../RawData/RawDataPage'
import { CSVLink } from 'react-csv'
import { Labels } from '../../common/labels/model'
import FixedTable from '@mulesoft/anypoint-components/lib/FixedTable'
import IconPanel from '@mulesoft/anypoint-icons/lib/Icon'
import PillMenu from '../PillMenu/PillMenu'
import Button from '@mulesoft/anypoint-components/lib/Button'
import Select from '@mulesoft/anypoint-components/lib/Select'
import './ReportsPage.css'
import ReportsMainSelector,
{ Props as ReportsMainSelectorProps } from '@mulesoft/monitoring-center-components/lib/components/Selector/ReportsMainSelector/ReportsMainSelector'
import BasicSelector, { BasicSelectorProps } from '@mulesoft/monitoring-center-components/lib/components/Selector/BasicSelector/BasicSelector'
import { supportedRuntimesText } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'
import MonitoringLocalStorageManager from '../../util/storage/MonitoringLocalStorageManager'
import { Application } from '../../remote-api/models'
import { isDashboardNotSupportedForAPIResourceType } from '../BuiltInDashboard/dashboardType'
import ResourceSelectorLabel
  from '@mulesoft/monitoring-center-components/lib/components/Selector/ResourceSelector/ResourceSelectorLabel/ResourceSelectorLabel'
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip'
import { getSupportedRuntimesText, supportedRuntimesTextForPCE } from '../../util/SupportedRuntimesHelper'
import { ARMApplication } from 'src/remote-api/models'
import { AlertResourceDeploymentType, ResourceType } from '../../common/resource/model'
import { isHybrid } from '../../components/BuiltInDashboard/common/hybrid'

export type ValueProps = {
  labels?: Labels,
  isLoading: boolean,
  organizationId: string,
  selectedPropsEnvironment?: string,
  environments: Environment[],
  environmentsLoading: boolean,
  applicationsData: ApplicationData[],
  applicationsARM?: ARMApplication[]
  applications?: Application[],
  reportsApplications?: ReportsApplication[],
  allBusinessGroupEnabled: boolean
}

export type ExternalProps = {
  selectedBusinessGroup: string,
  selectedEnvironment: string,
  selectedType: string,
  from: string,
  to: string,
}

export type EventProps = {
  initializeLoading: () => void,
  handleWrongOrgOrEnv: () => void,
  changeSelectedType: (selectedType: string) => void,
  fetchApplicationsData: (organizationId: string, envId: string, selectedType: string, dateRange: any) => void,
  selectApplication: (appId: string) => void,
  fetchApplications: (businessGroup: string, environment: string) => void,
  fetchApplicationsARM: (businessGroup: string, environment: string) => void,
  fetchEnvironments: (resourceSelectorContainer: ResourceSelectorContainer) => void,
  selectEnvironment: (resourceSelectorContainer: ResourceSelectorContainer) => void,
  changeDateRange: (dateRange: any) => void,
}

export type Props = ValueProps & ExternalProps

export type AllProps = Props & EventProps

export type State = {
  environmentLabel: string,
  businessGroup?: string,
  environment?: string,
}

export class ReportsPage extends React.Component<AllProps, State> {

  private selectedDateRange: { from: string, to: string }

  // Columns
  private appNameColumn = {
    key: 'appName',
    name: 'Application Name',
    width: 470,
    sort: appIdSort,
    renderer: (row: ApplicationData) => {
      const orgId = this.props.organizationId
      const envId = this.props.selectedEnvironment
      const app = this.getApplication(row.appId,row.targetId)
      const appName = row.appName || getApplicationName(row.appId, row.appName, this.props.applications, this.props.reportsApplications)

      const lsBuiltIn = MonitoringLocalStorageManager.get('builtIn')
      const dashboardType = lsBuiltIn && lsBuiltIn.dashboardType && isDashboardNotSupportedForAPIResourceType(lsBuiltIn.dashboardType) ?
        lsBuiltIn.dashboardType : 'overview'

      return (
        <div className={'application-link-container'}>
          {orgId === row.orgId && envId && app ?
            <a
              data-test-id={'app-link-container'}
              className={'app-link-container'}
              onClick={this.selectApplication.bind(this, app.id)}
              href={`#${getBuiltInURL(row.envId, dashboardType, row.appId, app.targetType.toLowerCase(), Number(app.targetId))}`}
              target={'_blank'}
            >
              <ResourceSelectorLabel maxLength={42} className={'reports-application-name'} label={appName}/>
            </a> :
            <ResourceSelectorLabel maxLength={42} className={'reports-application-name'} label={appName}/>
          }
        </div>)
    }
  }

  getTargetName(app: ARMApplication | undefined): string {
    let targetName
    if (!app) {
      return 'N/A'
    }
    switch (app.target.type.toLowerCase()) {
      case ResourceType.MC:
      case ResourceType.SERVER:
      case ResourceType.SERVER_GROUP:
      case ResourceType.CLUSTER:
      case ResourceType.HYBRID:
        targetName = app.target.name
        break
      case AlertResourceDeploymentType.CLOUDHUB.toLowerCase():
        targetName = 'CloudHub'
        break
      default:
        targetName = 'N/A'
        break
    }
    return targetName ? targetName : 'N/A'
  }

  private appTargetColumn = {
    key: 'targetId',
    name: 'Target Name',
    width: 250,
    renderer: (row: ApplicationData) => {
      const targetName = this.getTargetName(this.getApplicationARM(row.appId, row.targetId))
      return (
        <ResourceSelectorLabel maxLength={32} className={'reports-application-target-name'} label={targetName}/>
      )
    }
  }

  private organizationColumn = {
    key: 'orgId',
    name: 'Business group',
    width: 150,
    renderer: (row: ApplicationData) => {
      const organizations = this.props.labels ? this.props.labels.accessManager.organizations : undefined
      const organization = organizations ? organizations.find(org => org.id === row.orgId) : undefined
      const app = this.getApplication(row.appId, row.targetId) as ReportsApplication
      const orgName = row.orgName ? row.orgName : app ? app.orgName : organization ? organization.label : row.orgId

      return (<ResourceSelectorLabel maxLength={20} className={'reports-business-group-name-value'} label={orgName}/>)
    }
  }

  private environmentColumn = {
    key: 'envId',
    name: 'Environment',
    width: 150,
    renderer: (row: ApplicationData) => {
      const app = this.getApplication(row.appId, row.targetId) as ReportsApplication
      const environments = this.props.labels ? this.props.labels.accessManager.environments : undefined
      const environment = environments ? environments.find(env => env.id === row.envId) : undefined
      const envName = row.envName ? row.envName : app ? app.envName : environment ? environment.label : row.envId

      return (<ResourceSelectorLabel maxLength={20} className={'reports-environment-name-value'} label={envName}/>)
    }
  }

  private requestVolumeColumn = {
    key: 'requestVolume',
    name: 'Request volume',
    renderer: (row: ApplicationData) => {
      const requestVolume = row.requestVolume ? row.requestVolume : '0'
      return (
        <div data-test-id={'application-request-volume'} className={'application-request-volume'}>
          {parseInt(requestVolume)}
        </div>)
    }
  }

  private successfulRequestsColumn = {
    key: 'successfulRequests',
    name: 'Successful requests',
    renderer: (row: ApplicationData) => {
      const successfulRequests = row.successfulRequests ? row.successfulRequests : '0'
      return (
        <div data-test-id={'application-successful-requests'} className={'application-successful-requests'}>
          {parseInt(successfulRequests)}
        </div>)
    }
  }

  private requestsColumns = [
    this.appNameColumn,
    this.appTargetColumn,
    this.requestVolumeColumn,
    this.successfulRequestsColumn
  ]

  private performanceColumns = [
    this.appNameColumn,
    this.appTargetColumn,
    this.requestVolumeColumn,
    {
      key: 'responseTime',
      name: 'Response time',
      renderer: (row: ApplicationData) => {
        const responseTime = row.responseTime ? parseFloat(row.responseTime) : 0
        const parsedTime = responseTime < 1 ? (Math.round(responseTime * 1000) / 1000) : (Math.round(responseTime * 100) / 100)

        const timeLabel = parsedTime / 1000 < 1 ? `${parsedTime} ms` : `${Math.round(parsedTime / 1000)} s`

        return (
          <div data-test-id={'application-response-time'} className={'application-response-time'}>
            {timeLabel}
          </div>)
      }
    }
  ]

  private failuresColumns = [
    this.appNameColumn,
    this.appTargetColumn,
    this.requestVolumeColumn,
    {
      key: 'failedRequests',
      name: 'Failed requests',
      renderer: (row: ApplicationData) => {
        const failedRequests = row.failedRequests ? row.failedRequests : '0'
        return (
          <div data-test-id={'application-failed-requests'} className={'application-failed-requests'}>
            {parseInt(failedRequests)}
          </div>)
      }
    },
    this.successfulRequestsColumn
  ]

  private cpuUtilizationColumns = [
    this.appNameColumn,
    this.appTargetColumn,
    {
      key: 'cpuUtilization',
      name: 'CPU utilization',
      renderer: (row: ApplicationData) => {
        const cpuUtilization = row.cpuUtilization ? row.cpuUtilization : '0'
        return (
          <div data-test-id={'application-cpu-utilization'} className={'application-cpu-utilization'}>
            {Math.round(parseFloat(cpuUtilization) * 100) / 100}%
          </div>)
      }
    }
  ]

  private memoryUtilizationColumns = [
    this.appNameColumn,
    this.appTargetColumn,
    {
      key: 'memoryUtilization',
      name: 'Memory utilization',
      renderer: (row: ApplicationData) => {
        const memoryUtilization = row.memoryUtilization ? parseFloat(row.memoryUtilization) : 0
        return (
          <div data-test-id={'application-memory-utilization'} className={'application-memory-utilization'}>
            {getResolvedFileSize(memoryUtilization)}
          </div>)
      }
    },
    {
      key: 'totalMemory',
      name: 'Total memory',
      renderer: (row: ApplicationData) => {
        const totalMemory = row.totalMemory ? parseFloat(row.totalMemory) : 0
        return (
          <div data-test-id={'application-total-memory'} className={'application-total-memory'}>
            {getResolvedFileSize(totalMemory)}
          </div>)
      }
    },
    {
      key: 'memoryPressure',
      name: 'Memory pressure',
      renderer: (row: ApplicationData) => {
        const memoryPressure = row.memoryPressure ? row.memoryPressure : '0'
        return (
          <div data-test-id={'application-memory-pressure'} className={'application-memory-pressure'}>
            {Math.round(parseFloat(memoryPressure) * 100) / 100}%
          </div>)
      }
    }
  ]

  static getDefaultSortColumn(selectedType: string) {
    switch (selectedType) {
      case ReportsTypes.Requests:
        return 'requestVolume'
      case ReportsTypes.Performance:
        return 'responseTime'
      case ReportsTypes.Failures:
        return 'failedRequests'
      case ReportsTypes.CpuUtilization:
        return 'cpuUtilization'
      case ReportsTypes.MemoryUtilization:
        return 'memoryPressure'
      default:
        return 'successfulRequests'
    }
  }

  getColumns(selectedType: string) {
    let columns = []

    switch (selectedType) {
      case ReportsTypes.Requests:
        columns = this.requestsColumns
        break
      case ReportsTypes.Performance:
        columns = this.performanceColumns
        break
      case ReportsTypes.Failures:
        columns = this.failuresColumns
        break
      case ReportsTypes.CpuUtilization:
        columns = this.cpuUtilizationColumns
        break
      case ReportsTypes.MemoryUtilization:
        columns = this.memoryUtilizationColumns
        break
      default:
        columns = this.requestsColumns
        break
    }

    if (this.isReportsByAllBusinessGroupsEnabled() && this.props.selectedBusinessGroup === 'all') {
      const firstColumn = columns[0]
      const lastColumns = columns.slice(1, columns.length)

      columns = environmentsOptions.find(env => env.value === this.props.selectedEnvironment) ?
        [firstColumn].concat(this.organizationColumn).concat(this.environmentColumn).concat(lastColumns) :
        [firstColumn].concat(this.organizationColumn).concat(lastColumns)
    }

    return columns
  }

  // Business groups methods

  getBusinessGroupName() {
    const businessGroups = this.getResolvedBusinessGroups()
    const selectedBusinessGroup = businessGroups.find(bg => bg.value === this.props.selectedBusinessGroup)

    return selectedBusinessGroup ? selectedBusinessGroup.label.toLowerCase() : 'business group'
  }

  getResolvedBusinessGroups() {
    return [
      {value: this.props.organizationId, label: 'Current business group'},
      {value: 'all', label: 'All business groups'}
    ]
  }

  // Environments methods

  getEnvironmentName(environmentSelected?: string, envs?: Environment[]) {
    const {environments, selectedEnvironment} = this.props

    const resolvedEnvironments = envs ? envs : environments
    const selectedEnv = environmentSelected ? environmentSelected : selectedEnvironment

    const environment = resolvedEnvironments.find(env => env.id === selectedEnv)
    const environmentOption = environmentsOptions.find(e => e.value === selectedEnv)

    return environment ? environment.name : environmentOption ? environmentOption.label : 'environment'
  }

  getResolvedEnvironments(environments: Environment[]) {
    if (!environments) return [{value: '', label: 'Loading...'}]
    else if (this.state.businessGroup === 'all') return environmentsOptions
    else {
      return environments.map(env => {
        return {value: env.id, label: env.name}
      })
    }
  }

  // Applications methods

  selectApplication(appId: string) {
    this.props.selectApplication(appId)
  }

  getAppDetails(app: ReportsApplication, appId: string, targetId: string | undefined  ) {
    if(isHybrid(app.targetType) && app.targetId){
        return app.targetId.toString() === targetId && appId === app.name
    }
   return  app.id === appId || app.name === appId
  }

  getApplication(appId: string, targetId: string | undefined) {
    const {applications, reportsApplications} = this.props
    if (reportsApplications) {
      return reportsApplications.find(app => this.getAppDetails(app,appId,targetId))
    } else if (applications) {
      return applications.find(app => app.id === appId || app.name === appId)
    } else {
      return undefined
    }
  }

  getApplicationARM(appId: string, targetId: string | undefined) {
    const { applicationsARM } = this.props

    if (applicationsARM) {
      return applicationsARM.find(app => (
        (app.id === appId) ||
        (app.details.domain === appId) ||
        (app.target.id ? (app.target.id.toString() === targetId) : (app.artifact.name === appId))
      ))
    } else {
      return undefined
    }
  }

  // Component methods

  isReportsByAllBusinessGroupsEnabled() {
    return this.props.allBusinessGroupEnabled
  }

  constructor(props: AllProps) {
    super(props)

    this.isReportsByAllBusinessGroupsEnabled = this.isReportsByAllBusinessGroupsEnabled.bind(this)
    this.onChangeOrgSelect = this.onChangeOrgSelect.bind(this)
    this.onChangeEnvSelect = this.onChangeEnvSelect.bind(this)
    this.onChangeDateRange = this.onChangeDateRange.bind(this)
    this.onChangeSelectedType = this.onChangeSelectedType.bind(this)
    this.createReport = this.createReport.bind(this)
    this.getColumns = this.getColumns.bind(this)
    this.getBusinessGroupName = this.getBusinessGroupName.bind(this)
    this.getEnvironmentName = this.getEnvironmentName.bind(this)
    this.getResolvedBusinessGroups = this.getResolvedBusinessGroups.bind(this)
    this.getBusinessGroupSelectorProps = this.getBusinessGroupSelectorProps.bind(this)
    this.getResolvedEnvironments = this.getResolvedEnvironments.bind(this)
    this.getEnvironmentSelectorProps = this.getEnvironmentSelectorProps.bind(this)
    this.getReportsMainSelectorProps = this.getReportsMainSelectorProps.bind(this)
    this.getReportsSelector = this.getReportsSelector.bind(this)

    this.state = {
      businessGroup: this.isReportsByAllBusinessGroupsEnabled() ? props.selectedBusinessGroup : props.organizationId,
      environment: !this.isReportsByAllBusinessGroupsEnabled() && environmentsOptions.find(opt => opt.value === props.selectedEnvironment) ?
        undefined : props.selectedEnvironment,
      environmentLabel: this.getEnvironmentName(props.selectedEnvironment)
    }

    this.selectedDateRange = {from: props.from, to: props.to}
  }

  isSpecificEnvironment(environment: string) {
    return !environmentsOptions.find(opt => opt.value === environment)
  }

  componentDidMount() {
    if ((this.props.selectedBusinessGroup !== 'all' && this.props.selectedBusinessGroup !== this.props.organizationId) ||
      this.state.businessGroup !== this.props.selectedBusinessGroup
    ) {
      this.props.handleWrongOrgOrEnv()
    }
    if (!this.props.reportsApplications) {
      this.props.fetchApplications(this.props.selectedBusinessGroup, this.props.selectedEnvironment)
    }
    if (!this.props.selectedPropsEnvironment) {
      const resourceSelectorContainer = {orgId: this.props.organizationId, isLogFiles: true}
      this.props.fetchEnvironments(resourceSelectorContainer)
    }

    if (this.state.businessGroup && this.state.environment) {
      this.props.fetchApplicationsData(this.state.businessGroup, this.state.environment, this.props.selectedType, this.selectedDateRange)
      this.props.fetchApplicationsARM( this.state.businessGroup, this.state.environment)
    }

    if (!this.state.environment && this.props.applicationsData) this.props.fetchApplicationsData('', '', '', {})
  }

  componentWillReceiveProps(nextProps: Readonly<AllProps>, nextContext: any): void {
    if (nextProps.selectedEnvironment !== this.props.selectedEnvironment) {
      this.setState({...this.state, environmentLabel: this.getEnvironmentName(nextProps.selectedEnvironment)})
    }
    if (nextProps.environments !== this.props.environments) {
      this.setState({...this.state, environmentLabel: this.getEnvironmentName(nextProps.selectedEnvironment, nextProps.environments)})
    }
  }

  createReport() {
    const businessGroup = this.state.businessGroup
    const env = this.state.environment

    if (businessGroup && env) {
      const reportsType = this.props.selectedType
      const from = this.selectedDateRange.from
      const to = this.selectedDateRange.to

      const hashArray = window.location.hash.split('/')
      const hashType = hashArray[hashArray.length - 1]
      const resolvedType = reportsType !== hashType ? hashType : reportsType

      window.location.hash = `#/reports/organizations/${businessGroup}/environments/${env}/from/${from}/to/${to}/type/${resolvedType}`
      this.props.fetchApplications(businessGroup, env)
      this.props.fetchApplicationsData(businessGroup, env, resolvedType, this.selectedDateRange)
      this.props.fetchApplicationsARM(businessGroup, env)
    }
  }

  getReportsSelector(environments: Environment[], environmentsLoading: boolean) {
    if (this.isReportsByAllBusinessGroupsEnabled()) {
      const reportsMainSelectorProps = this.getReportsMainSelectorProps(environments, environmentsLoading)

      return (
        <ReportsMainSelector {...reportsMainSelectorProps} />
      )
    } else {
      const environmentSelectorProps = this.getEnvironmentSelectorProps(environments)

      return (
        <div className="monitoring-reports-main-selector">
          <div className="monitoring-main-selector-modal-body-environment-selector">
            <span className="monitoring-environment-selector-label">{this.state.businessGroup === 'all' ? 'Environment type' : 'Environment'}</span>
            <BasicSelector {...environmentSelectorProps}/>
          </div>
          <Button kind="primary" onClick={this.createReport} disabled={!(environmentSelectorProps.value !== undefined)}>View report</Button>
        </div>

      )
    }
  }

  render() {
    const {isLoading, environments, environmentsLoading, selectedBusinessGroup, selectedEnvironment, selectedType, applicationsData} = this.props

    if (this.isSpecificEnvironment(selectedEnvironment) && environments && !environments.find(env => env.id === selectedEnvironment)) {
      this.props.handleWrongOrgOrEnv()
    }

    const selectedDateRange = this.selectedDateRange
    const dateRange = reportsTimeOptions.find(date => date.value.from === selectedDateRange.from)
    const resolvedDate = {value: selectedDateRange, label: dateRange ? dateRange.label : undefined}
    const resolvedDatePlaceholder = resolvedDate.label
    const resolvedSelectedBusinessGroup = this.isReportsByAllBusinessGroupsEnabled() ? selectedBusinessGroup : this.state.businessGroup

    return (
      <div className={'reports-page-container'}>
        <div className={'reports-page-header'}>
          <div className={'reports-page-header-selector'}>
            <div className={'top-bar'}>
              {this.getReportsSelector(environments, environmentsLoading)}

              <div className={'selects-options'}>
                <Select
                  testId={'reports-datepicker'}
                  className={'reports-date-picker'}
                  options={reportsTimeOptions}
                  onChange={this.onChangeDateRange}
                  value={resolvedDate}
                  placeholder={resolvedDatePlaceholder}
                />
                <Tooltip className={'reports-refresh-data-tooltip'} content={'Refresh data'} position={'bottom'}>
                  <Button kind="tertiary" noFill={true} className="refresh-data-button" testId="refresh-data-button" onClick={this.createReport}>
                      <IconPanel
                        testId={'refresh-data-icon'}
                        fill={'#3A3B3C'}
                        className={isLoading ? 'refresh-data-icon refresh-loading' : 'refresh-data-icon'}
                        name={'refresh'}
                        size={24}
                      />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        <div className={'reports-page-body'}>
          <div className={'reports-page-body-container'}>
            <div className={'pill-menu'}>
              {this.renderPillMenu(selectedType)}
              {resolvedSelectedBusinessGroup && selectedEnvironment && this.renderInfo(applicationsData, selectedType)}
            </div>
            {isLoading ? RawDataPage.renderLoadingState() : this.renderSuccessState(applicationsData, selectedType)}
          </div>

        </div>
      </div>
    )
  }

  renderSuccessState(applicationsData: ApplicationData[], selectedType: string) {
    const columns = this.getColumns(selectedType)
    const defaultSortColumn = ReportsPage.getDefaultSortColumn(selectedType)

    return (
      <div className={'reports-list'}>
        <FixedTable
          data-test-id={'reports-list-table'}
          columns={columns}
          data={applicationsData}
          defaultSortColumn={defaultSortColumn}
          defaultSortAsc={false}
          emptyStateMessage={'No data found for the selected time. Try a different date range.'}
        />
      </div>
    )
  }

  renderPillMenu(selectedType: string) {
    const buttons = isPCE() ? pceReportsButtons : reportsButtons

    return (
      <div className={'menu'}>
        <PillMenu
          data-test-id={'reports-pill-menu'}
          buttons={buttons}
          selected={selectedType}
          disableAll={false}
          onClick={this.onChangeSelectedType}
        />
      </div>
    )
  }

  renderInfo(applicationsData: ApplicationData[], selectedType: string) {
    const columns = this.getColumns(selectedType)
    const resolvedColumns = columns.map(col => {
      return {label: col.name, key: col.key}
    })
    const resolvedData = applicationsData && applicationsData.length > 0 ? this.getSpecificArray(applicationsData, resolvedColumns) : undefined

    return (
      <div className={'reports-info-container'}>
        {resolvedData &&
        <Button kind="tertiary" noFill={true} className="export-csv-button" testId="export-csv-button">
          <CSVLink
            data-test-id="export-csv-link"
            className="export-csv-link"
            filename={'AnypointMonitoringExport.csv'}
            data={resolvedData}
            headers={resolvedColumns}
          >
            <IconPanel testId={'download-csv-icon'} className={'download-csv-icon'} name={'download'} size={18} fill={'#6B6C6D'}/>
            <span data-test-id="export-csv-text" className="export-csv-text">Export CSV</span>
          </CSVLink>
        </Button>
        }
      </div>
    )
  }

  // Selectors methods

  onChangeSelectedType(selectedType: string) {
    this.props.changeSelectedType(selectedType)
    this.createReport()
  }

  onChangeDateRange(newDate: any) {
    const date = newDate.itemsSelected
    this.selectedDateRange = date.value
    this.props.changeDateRange(this.selectedDateRange)

    this.createReport()
  }

  onChangeOrgSelect(result: any) {
    this.setState({...this.state, businessGroup: result.value, environment: undefined})
  }

  onChangeEnvSelect(result: any) {
    this.setState({...this.state, environment: result.value})
  }

  getEnvironmentSelectorValueRenderer(maxLength: number, label: string, className: string) {
    return () => (<ResourceSelectorLabel maxLength={maxLength} label={label} className={className} tooltipOffset={[62, 0]}/>)
  }

  getReportsMainSelectorProps(environments: Environment[], environmentsLoading: boolean): ReportsMainSelectorProps {
    return {
      businessGroupSelectorProps: this.getBusinessGroupSelectorProps(),
      selectedBusinessGroup: this.props.selectedBusinessGroup,
      environmentSelectorProps: this.getEnvironmentSelectorProps(environments),
      selectedEnvironment: this.state.environmentLabel,
      environmentSelectorText: this.state.businessGroup === 'all' ? 'Environment type' : 'Environment',
      isLoading: environmentsLoading,
      onView: this.createReport,
      supportedRuntimesTooltipText: getSupportedRuntimesText()
    }
  }

  getBusinessGroupSelectorProps(): BasicSelectorProps {
    const resolvedBusinessGroups = this.getResolvedBusinessGroups()

    return {
      maxLength: 60,
      className: 'business-group-select',
      testId: 'business-group-select',
      name: 'business-group',
      options: resolvedBusinessGroups,
      placeholder: 'Select business group',
      onChange: this.onChangeOrgSelect,
      value: this.state.businessGroup
    }
  }

  getEnvironmentSelectorProps(environments: Environment[]): BasicSelectorProps {
    const resolvedEnvironments = this.getResolvedEnvironments(environments)
    const selectedEnv = resolvedEnvironments.find(env => env.value === this.state.environment)
    const valueRendererLabel = selectedEnv ? selectedEnv.label : this.state.environmentLabel

    return {
      maxLength: 48,
      valueRenderer: this.getEnvironmentSelectorValueRenderer(48, valueRendererLabel, 'reports-environment-value-renderer'),
      className: 'environment-select',
      testId: 'environment-select',
      name: 'environment',
      options: resolvedEnvironments,
      placeholder: this.state.businessGroup === 'all' ? 'Select environment type' : 'Select environment',
      onChange: this.onChangeEnvSelect,
      value: this.state.environment
    }
  }

  // Export CSV methods
  getSpecificArray(applicationsData: ApplicationData[], resolvedColumns: any[]) {
    return applicationsData.map(appData => {
      let result = {}
      resolvedColumns.forEach(col => {
        result[col.key] = appData[col.key] ? appData[col.key].toString() : ''
      })
      return result
    })
  }

}
